import { useAuth } from '@/context/AuthContext';
import type { BubbleColorMenuItem } from '@/lib/editor/tailwind/selectors/color-selector';

import { cn } from '@/utils/utils';
import {
  type Annotation,
  AnnotationHighlightLayer,
  type AnnotationTooltipContentProps,
  CanvasLayer,
  HighlightLayer,
  type HighlightRect,
  Page,
  Pages,
  SelectionTooltip,
  TextLayer,
  useAnnotations,
  usePdfJump,
  useSelectionDimensions,
} from '@anaralabs/lector';
import type { RouterOutputs } from '@unriddle/api';
import { atom, useAtom, useSetAtom } from 'jotai';
import { atomFamily, atomWithStorage, useAtomCallback } from 'jotai/utils';
import { useTheme } from 'next-themes';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import { PDFViewerFun } from '../PDFViewerFun';
import { HoverTooltipContent } from './annotation-hover-tooltip';
import { TooltipContent } from './annotation-tooltip';
import { SelectionActions } from './selection-actions';
import {
  convertToClientAnnotation,
  useDocumentAnnotations,
} from './use-document-annotations';

export const annotationNavigationAtom = atom<
  ((annotation: Annotation) => void) | null
>(null);

export const focusedAnnotationIdAtom = atom<string | undefined>(undefined);
export const focusedHoverAnnotationIdAtom = atom<string | undefined>(undefined);

interface PDFContentProps {
  chatbotId: string;
  defaultAnnotations?: RouterOutputs['documentAnnotation']['all']['annotations'];
}

const offsetAtomFamily = atomFamily((id: string) => atomWithStorage(id, 0));

export const lastHighlightColorAtom = atomWithStorage<BubbleColorMenuItem>(
  'lastHighlightColor',
  {
    name: 'Yellow',
    color: 'var(--highlight-yellow)',
    borderColor: 'var(--highlight-yellow-border)',
  },
);

export const PdfContent = ({
  chatbotId,
  defaultAnnotations,
}: PDFContentProps) => {
  const {
    handleCreateAnnotation,
    handleAnnotationClick,
    handleAnnotationHover,
  } = useDocumentAnnotations(chatbotId);
  const [focusedAnnotationId] = useAtom(focusedAnnotationIdAtom);
  const [focusedHoverAnnotationId] = useAtom(focusedHoverAnnotationIdAtom);
  const { setAnnotations } = useAnnotations();
  const { annotations } = useAnnotations();
  const { getSelection } = useSelectionDimensions();
  const { scrollToHighlightRects } = usePdfJump();
  const { user } = useAuth();
  const setNavigationFunction = useSetAtom(annotationNavigationAtom);
  const [lastHighlightColor, _] = useAtom(lastHighlightColorAtom);

  useEffect(() => {
    const navigateToAnnotation = (annotation: Annotation) => {
      if (annotation.comment) {
        handleAnnotationHover(annotation);
        handleAnnotationClick(null);
      } else {
        handleAnnotationClick(annotation);
        handleAnnotationHover(null);
      }
      if (annotation.highlights) {
        scrollToHighlightRects(annotation.highlights, 'pixels', 'center', -50);
      }
    };
    setNavigationFunction(() => navigateToAnnotation);
    return () => setNavigationFunction(null);
  }, [handleAnnotationClick, handleAnnotationHover, scrollToHighlightRects]);

  const handleCreateHighlight = () => {
    const selection = getSelection();
    if (!selection || !selection.highlights.length) return;

    const newAnnotation = {
      pageNumber: selection.highlights[0].pageNumber,
      highlights: selection.highlights,
      color: lastHighlightColor.color,
      borderColor: lastHighlightColor.borderColor,
      updatedAt: new Date(),
      createdAt: new Date(),
      comment: '',
      metadata: {
        chatbotId,
        text: selection.text,
        userId: user?.id,
        userDisplayName: user?.displayName,
      },
    };

    handleCreateAnnotation(newAnnotation);
    window.getSelection()?.removeAllRanges();
  };

  useEffect(() => {
    const clientAnnotations = defaultAnnotations?.map((annotation) => ({
      ...convertToClientAnnotation({
        ...annotation,
        highlights: annotation.highlights as HighlightRect[],
      }),
    }));
    setAnnotations(clientAnnotations ?? []);
  }, []);

  useEffect(() => {
    if (!focusedAnnotationId) return;

    const annotation = annotations.find((a) => a.id === focusedAnnotationId);
    if (!annotation || !annotation?.highlights?.length) return;

    scrollToHighlightRects(annotation.highlights, 'pixels', 'center', 100);
  }, [focusedAnnotationId, annotations, scrollToHighlightRects]);

  const handlePagesClick = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;

      if (target.closest('[role="tooltip"]')) {
        return;
      }

      const clickedHighlight = target.closest('[data-highlight-id]');

      if (clickedHighlight) {
        return;
      }

      if (focusedAnnotationId) {
        handleAnnotationClick(null);
      }
      if (focusedHoverAnnotationId) {
        handleAnnotationHover(null);
      }
    },
    [focusedAnnotationId, focusedHoverAnnotationId],
  );

  const virtualizerOptions = useMemo(() => ({ overscan: 2 }), []);
  const renderTooltipContent = useCallback(
    (props: AnnotationTooltipContentProps) => {
      return (
        <TooltipContent
          {...props}
          onClose={() => handleAnnotationClick(null)}
        />
      );
    },
    [],
  );
  const renderHoverTooltipContent = useCallback(
    ({
      annotation,
    }: {
      annotation: Annotation;
      onClose: () => void;
    }) => {
      return (
        <HoverTooltipContent
          annotation={annotation}
          onClose={() => handleAnnotationClick(null)}
        />
      );
    },
    [],
  );

  const { resolvedTheme } = useTheme();

  const atom = useMemo(() => offsetAtomFamily(chatbotId), [chatbotId]);

  // Get refs for the initial offset and the setter function
  const initialOffsetRef = useRef<number | null>(null);
  const setOffsetRef = useRef<((value: number) => void) | null>(null);

  // Create a callback to get the atom value imperatively
  const readAtom = useAtomCallback(
    useCallback(
      (get, set) => {
        const value = get(atom);
        initialOffsetRef.current = value;

        // Store the setter function in the ref
        setOffsetRef.current = (newValue: number) => set(atom, newValue);

        return value;
      },
      [atom],
    ),
  );

  // Run only once during initialization
  useLayoutEffect(() => {
    readAtom();
  }, [readAtom]);

  // Handler for offset changes
  const handleOffsetChange = (offset: number) => {
    if (setOffsetRef.current) {
      setOffsetRef.current(offset);
    }
  };

  return (
    <>
      <Pages
        initialOffset={initialOffsetRef.current ?? 0}
        onOffsetChange={handleOffsetChange}
        className="w-full"
        onClick={handlePagesClick}
        style={{ scrollbarWidth: 'thin' }}
        virtualizerOptions={virtualizerOptions}
      >
        <Page>
          <PDFViewerFun />
          <div
            className={cn(
              resolvedTheme === 'dark' &&
                'invert-[91%] hue-rotate-180 brightness-[80%] contrast-[228%] bg-background',
            )}
          >
            <CanvasLayer />
            <TextLayer className="mix-blend-multiply" />
            <AnnotationHighlightLayer
              focusedAnnotationId={focusedAnnotationId}
              focusedHoverAnnotationId={focusedHoverAnnotationId}
              hoverTooltipClassName="z-50"
              highlightClassName="dark:opacity-30 mix-blend-multiply transition-all duration-200 cursor-pointer"
              onAnnotationClick={handleAnnotationClick}
              renderHoverTooltipContent={renderHoverTooltipContent}
              renderTooltipContent={renderTooltipContent}
            />
          </div>
          <HighlightLayer className="bg-highlightYellow z-10 opacity-40 brightness-120 highlight-area" />
        </Page>
      </Pages>
      <SelectionTooltip>
        <SelectionActions
          chatbotId={chatbotId}
          onHighlight={handleCreateHighlight}
        />
      </SelectionTooltip>
    </>
  );
};
